(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let revealObserver;
  let frame;
  let parallaxItems = [];
  let activeParallax = new Set();
  const parallaxObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => entry.isIntersecting ? activeParallax.add(entry.target) : activeParallax.delete(entry.target));
    requestParallax();
  }, { rootMargin: '80px' }) : null;

  function drawParallax() {
    frame = null;
    if (reducedMotion.matches) return;
    const viewport = window.innerHeight;
    activeParallax.forEach((item) => {
      if (!item.isConnected) { activeParallax.delete(item); return; }
      const bounds = item.getBoundingClientRect();
      const progress = Math.max(-1, Math.min(1, (viewport / 2 - bounds.top - bounds.height / 2) / viewport));
      item.style.setProperty('--fairy-parallax', `${(progress * Number(item.dataset.fairyParallax || 16)).toFixed(1)}px`);
    });
  }

  function requestParallax() {
    if (!frame && !reducedMotion.matches) frame = requestAnimationFrame(drawParallax);
  }

  function initializeMotion(scope = document) {
    if (reducedMotion.matches || !('IntersectionObserver' in window)) return;
    document.documentElement.classList.add('fairy-motion-enabled');
    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -18px' });
    scope.querySelectorAll('[data-fairy-reveal]').forEach((element) => revealObserver.observe(element));
    scope.querySelectorAll('[data-fairy-parallax]').forEach((element) => {
      if (parallaxItems.includes(element)) return;
      parallaxItems.push(element);
      parallaxObserver?.observe(element);
    });
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    scope.querySelectorAll('[data-fairy-depth]:not([data-fairy-depth-ready])').forEach((scene) => {
      scene.dataset.fairyDepthReady = 'true';
      scene.addEventListener('pointermove', (event) => {
        if (reducedMotion.matches) return;
        const bounds = scene.getBoundingClientRect();
        scene.style.setProperty('--fairy-tilt-x', `${((event.clientX - bounds.left) / bounds.width - .5) * 12}px`);
        scene.style.setProperty('--fairy-tilt-y', `${((event.clientY - bounds.top) / bounds.height - .5) * 12}px`);
      }, { passive: true });
      scene.addEventListener('pointerleave', () => {
        scene.style.setProperty('--fairy-tilt-x', '0px');
        scene.style.setProperty('--fairy-tilt-y', '0px');
      });
    });
  }

  if (!customElements.get('fairy-sticky-buy')) {
    customElements.define('fairy-sticky-buy', class extends HTMLElement {
      connectedCallback() {
        this.button = this.querySelector('button');
        this.price = this.querySelector('[data-sticky-price]');
        this.productInfo = document.querySelector(`product-info[data-section="${this.dataset.section}"]`);
        this.form = document.getElementById(this.dataset.form);
        if (!this.form || !this.productInfo || !('IntersectionObserver' in window)) return;
        this.original = this.form.querySelector('[name="add"]');
        if (!this.original) return;
        this.sync = () => {
          const nextOriginal = document.getElementById(this.dataset.form)?.querySelector('[name="add"]');
          if (nextOriginal !== this.original) {
            if (this.original) this.visibility?.unobserve(this.original);
            this.original = nextOriginal;
            if (this.original) this.visibility?.observe(this.original);
          }
          if (!this.original) { this.button.disabled = true; return; }
          const disabled = this.original.disabled || this.original.getAttribute('aria-disabled') === 'true';
          if (this.button.disabled !== disabled) this.button.disabled = disabled;
          const label = this.original.querySelector('span');
          const buttonText = label?.textContent.trim() || this.original.textContent.trim();
          if (this.button.textContent.trim() !== buttonText) this.button.textContent = buttonText;
          const price = this.productInfo.querySelector('.price:not(.price--on-sale) .price__regular .price-item--regular, .price--on-sale .price__sale .price-item--sale');
          if (price && this.price.textContent.trim() !== price.textContent.trim()) this.price.textContent = price.textContent.trim();
        };
        this.onClick = () => {
          const form = document.getElementById(this.dataset.form);
          const submit = form?.querySelector('[name="add"]');
          if (!form || !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true') return;
          if (!form.reportValidity()) return;
          form.requestSubmit(submit);
        };
        this.button.addEventListener('click', this.onClick);
        this.observer = new MutationObserver(this.sync);
        this.observer.observe(this.productInfo, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class'] });
        this.visibility = new IntersectionObserver(([entry]) => {
          const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.classList.toggle('is-visible', visible);
          document.body.classList.toggle('fairy-sticky-active', visible);
        });
        this.visibility.observe(this.original);
        this.sync();
      }
      disconnectedCallback() {
        this.observer?.disconnect();
        this.visibility?.disconnect();
        this.button?.removeEventListener('click', this.onClick);
        document.body.classList.remove('fairy-sticky-active');
      }
    });
  }

  initializeMotion();
  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax, { passive: true });
  document.addEventListener('shopify:section:load', (event) => initializeMotion(event.target));
  document.addEventListener('shopify:section:select', (event) => event.target.querySelectorAll('[data-fairy-reveal]').forEach((item) => item.classList.add('is-visible')));
  reducedMotion.addEventListener('change', () => {
    if (reducedMotion.matches) {
      document.documentElement.classList.remove('fairy-motion-enabled');
      parallaxItems.forEach((item) => item.style.removeProperty('--fairy-parallax'));
      document.querySelectorAll('[data-fairy-depth]').forEach((item) => { item.style.removeProperty('--fairy-tilt-x'); item.style.removeProperty('--fairy-tilt-y'); });
    } else initializeMotion();
  });
})();
